/**
 * Shadow Utilities
 *
 * Helper functions for converting shadow tokens to CSS and platform-specific formats.
 */

import { shadows, type ShadowKey } from "./index";

/**
 * Convert shadow token to CSS box-shadow string
 */
export const toBoxShadow = (key: ShadowKey): string => {
  const shadow = shadows[key];
  return shadow.layers
    .map(
      (layer) =>
        `${layer.offsetX}px ${layer.offsetY}px ${layer.blurRadius}px ${layer.spreadRadius}px ${layer.color}`,
    )
    .join(", ");
};

/**
 * Pre-computed CSS box-shadow values for all shadow tokens
 */
export const boxShadows = {
  card: toBoxShadow("card"),
  "card-hovered": toBoxShadow("card-hovered"),
  bigcard: toBoxShadow("bigcard"),
  "bigcard-hovered": toBoxShadow("bigcard-hovered"),
  topbar: toBoxShadow("topbar"),
  bottombar: toBoxShadow("bottombar"),
  modal: toBoxShadow("modal"),
  dropdown: toBoxShadow("dropdown"),
  fab: toBoxShadow("fab"),
  "fab-pressed": toBoxShadow("fab-pressed"),
  knob: toBoxShadow("knob"),
} as const;

/**
 * CSS custom properties for shadows
 */
export const shadowCSSVariables = `
:root {
  --shadow-card: ${boxShadows.card};
  --shadow-card-hovered: ${boxShadows["card-hovered"]};
  --shadow-bigcard: ${boxShadows.bigcard};
  --shadow-bigcard-hovered: ${boxShadows["bigcard-hovered"]};
  --shadow-topbar: ${boxShadows.topbar};
  --shadow-bottombar: ${boxShadows.bottombar};
  --shadow-modal: ${boxShadows.modal};
  --shadow-dropdown: ${boxShadows.dropdown};
  --shadow-fab: ${boxShadows.fab};
  --shadow-fab-pressed: ${boxShadows["fab-pressed"]};
  --shadow-knob: ${boxShadows.knob};
}
`.trim();

export type BoxShadowKey = keyof typeof boxShadows;
